import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Star, Users, DoorOpen, Clock, IndianRupee, Stethoscope, ArrowRight, Activity } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Doctor card used on the Hospital Public Profile grid.
 * Polls the doctor's queue snapshot every 15s so the "Current Token" widget
 * stays live for patients scanning the page from the clinic's front-desk QR.
 */
export default function DoctorDirectoryCard({ doctor, lang = "en" }) {
  const navigate = useNavigate();
  const [queue, setQueue] = useState(doctor.queue || {});
  const [availability, setAvailability] = useState(doctor.availability || {});
  const timerRef = useRef(null);
  const isHi = lang === "hi";
  const isFull = !!availability.is_full;

  useEffect(() => {
    // Refresh queue + availability slice — kept lighter than re-fetching the
    // full hospital payload so hospitals with 10+ docs poll cheaply.
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/public/doctors/${doctor.id}`);
        if (data?.queue) setQueue(data.queue);
      } catch (_) { /* graceful fail — keep previous snapshot */ }
      try {
        const { data: a } = await axios.get(`${API}/public/doctors/${doctor.id}/availability`);
        if (a) setAvailability(a);
      } catch (_) { /* graceful */ }
    };
    timerRef.current = setInterval(tick, 15000);
    return () => clearInterval(timerRef.current);
  }, [doctor.id]);

  const waitMin = doctor.avg_wait_time ?? 15;
  const est = queue?.waiting_count ? queue.waiting_count * waitMin : waitMin;

  return (
    <Card className="medical-card overflow-hidden flex flex-col" data-testid={`doctor-card-${doctor.id}`}>
      {/* Header row: photo + name */}
      <div className="p-5 pb-4 flex items-start gap-4">
        <div className="w-16 h-16 rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center shrink-0 ring-1 ring-slate-200">
          {doctor.profile_pic ? (
            <img src={doctor.profile_pic} alt={doctor.name} className="w-full h-full object-cover" />
          ) : (
            <Stethoscope className="w-7 h-7 text-slate-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h3 className="font-heading font-semibold text-slate-900 text-base" data-testid="doc-card-name">{doctor.name}</h3>
            {doctor.verified && (
              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 gap-1 h-5 px-1.5 text-[10px]" data-testid="doc-card-nmc">
                <ShieldCheck className="w-3 h-3" /> NMC
              </Badge>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5" data-testid="doc-card-spec">
            {doctor.specialization_en}
            {doctor.specialization_hi && <span className="text-slate-400"> · {doctor.specialization_hi}</span>}
          </p>
          <div className="mt-1.5 flex items-center gap-2.5 text-[11px] text-slate-500 flex-wrap">
            {(doctor.rating?.count || 0) > 0 && (
              <span className="inline-flex items-center gap-0.5" data-testid="doc-card-rating">
                <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                <b className="text-slate-700">{doctor.rating.avg?.toFixed(1)}</b>
                <span>({doctor.rating.count})</span>
              </span>
            )}
            {(doctor.trust_score || 0) > 0 && (
              <span className="inline-flex items-center gap-0.5">
                <Users className="w-3 h-3 text-sky-500" /> {doctor.trust_score} refs
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Live Queue Widget */}
      <div className="mx-5 rounded-lg bg-gradient-to-br from-sky-50 to-emerald-50 border border-sky-100 p-3 mb-4"
           data-testid={`doc-card-queue-${doctor.id}`}>
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-[10px] tracking-[0.14em] uppercase text-sky-700 font-semibold flex items-center gap-1">
              <Activity className="w-3 h-3" />
              {isHi ? "Live Queue" : "Live Queue"}
            </p>
            <p className="text-lg font-heading font-bold text-slate-900 mt-0.5">
              {queue?.current ? `Token #${queue.current}` : (isHi ? "उपलब्ध" : "Available")}
            </p>
            <p className="text-[11px] text-slate-500">
              {(queue?.waiting_count ?? 0)} {isHi ? "आगे में" : "waiting"} · ~{est} min
            </p>
          </div>
          <Clock className="w-8 h-8 text-emerald-400/70" />
        </div>
      </div>

      {/* Meta chips */}
      <div className="px-5 pb-4 grid grid-cols-2 gap-2 text-[11px]">
        {doctor.consultation_fee && (
          <span className="inline-flex items-center gap-1 text-slate-600">
            <IndianRupee className="w-3 h-3" /> ₹{doctor.consultation_fee}
          </span>
        )}
        {doctor.room_number && (
          <span className="inline-flex items-center gap-1 text-slate-600">
            <DoorOpen className="w-3 h-3" /> Room {doctor.room_number}
          </span>
        )}
      </div>

      {/* Instant Book Button */}
      <div className="px-5 pb-5 mt-auto">
        {isFull ? (
          <>
            <Button
              data-testid={`doc-card-book-${doctor.id}`}
              disabled
              className="w-full bg-slate-200 text-slate-500 cursor-not-allowed gap-1.5 h-11 tap-target"
            >
              <span aria-hidden>🚫</span> {isHi ? "आज की सीटें फुल हैं" : "Today's Bookings Full"}
            </Button>
            <p className="text-[11px] text-rose-600 mt-2 text-center" data-testid={`doc-card-full-notice-${doctor.id}`}>
              {isHi
                ? "अगले उपलब्ध दिन के लिए बाद में देखें।"
                : "Please try the next available day."}
            </p>
          </>
        ) : (
          <Button
            data-testid={`doc-card-book-${doctor.id}`}
            onClick={() => navigate(`/patient/doctor/${doctor.id}`)}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 h-11 tap-target">
            {isHi ? "अपॉइंटमेंट बुक करें" : "Book Appointment"} <ArrowRight className="w-4 h-4" />
          </Button>
        )}
      </div>
    </Card>
  );
}
